import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';

// The world editor of the staff (client: worldEditorService.ts, opened with its own key, F4 by default):
// a build-mode panel docked beside the world. The catalog is a tree (category, family, group) next to the
// objects it holds, with a search, a plugin filter, favourites and recents. Placing steps the panel aside
// and brings it back when the placement ends, so putting down several objects in a row is one click each.
// What cannot be placed is never listed: the server rules it out when it reads the plugins, and an object
// whose preview fails is put aside by itself (the "Écartés" view lists them, they can be restored).

interface Item {
  id: number;
  n: string;
  edid: string;
  t?: string;
  c: string;
  mod: string;
}

interface Facet {
  id: string;
  label: string;
  count: number;
}

interface ResultsDetail {
  ready: boolean;
  query: string;
  category: string;
  family: string;
  group: string;
  mod: string;
  offset: number;
  total: number;
  counts: Record<string, number>;
  families: Facet[];
  groups: Facet[];
  mods: Facet[];
  results: Item[];
}

interface PlacedEntry {
  r: number;
  n: string;
  c: string;
  pos: number[];
  by: string;
  at: number;
  sc?: number;
}

interface HiddenEntry {
  r: number;
  n: string;
  edid?: string;
  mod?: string;
  pos: number[];
  by: string;
  at: number;
}

type Mode = 'catalog' | 'placed' | 'map' | 'broken';
type Shelf = 'all' | 'fav' | 'recent';

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'craft', label: 'Postes de craft' },
  { id: 'furniture', label: 'Meubles' },
  { id: 'container', label: 'Conteneurs' },
  { id: 'light', label: 'Lumières' },
  { id: 'decor', label: 'Décor' },
  { id: 'activator', label: 'Activateurs' },
];

const categoryLabel = (id: string): string => CATEGORIES.find((c) => c.id === id)?.label ?? id;

const PAGE = 60;
const SEARCH_DEBOUNCE_MS = 220;
const NOT_READY_RETRY_MS = 2000;
// A placement that never showed its HUD (the server refused it) must not leave the editor closed for good
const PLACEMENT_START_GRACE_MS = 4000;
const MAX_RECENTS = 24;
const STORE_KEY = 'skymp-world-editor';

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'world-editor', ...payload }));
  } catch (e) {
    // Running outside the game (e.g. Storybook)
  }
};

// An editor id has no spaces ("TavernChair01"): cut it where the words start, for the name the eye reads
const prettify = (item: { n: string; edid: string }): string => {
  if (item.n !== item.edid) return item.n;
  return item.n
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
};

const monogram = (name: string): string => {
  const letters = name.replace(/[^A-Za-zÀ-ÿ]/g, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
};

interface Store {
  fav: Item[];
  recent: Item[];
}

const loadStore = (): Store => {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { fav: Array.isArray(parsed.fav) ? parsed.fav : [], recent: Array.isArray(parsed.recent) ? parsed.recent : [] };
    }
  } catch (e) {
    // no storage: the lists last for the session only
  }
  return { fav: [], recent: [] };
};

const saveStore = (s: Store): void => {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(s));
  } catch (e) {
    // see loadStore
  }
};

const formatDate = (epochMs: number): string => {
  if (!epochMs) return '';
  try {
    return new Date(epochMs).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

const slim = (o: Item): Item => ({ id: o.id, n: o.n, edid: o.edid, c: o.c, mod: o.mod });

const WorldEditor: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<Mode>('catalog');
  const [shelf, setShelf] = useState<Shelf>('all');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [family, setFamily] = useState('all');
  const [group, setGroup] = useState('all');
  const [mod, setMod] = useState('all');
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [families, setFamilies] = useState<Facet[]>([]);
  const [groups, setGroups] = useState<Facet[]>([]);
  const [mods, setMods] = useState<Facet[]>([]);
  const [ready, setReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Item | null>(null);
  const [store, setStore] = useState<Store>(loadStore);
  const [placed, setPlaced] = useState<PlacedEntry[] | null>(null);
  const [placedTotal, setPlacedTotal] = useState(0);
  const [placedFilter, setPlacedFilter] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const [hidden, setHidden] = useState<HiddenEntry[] | null>(null);
  const [hiddenTotal, setHiddenTotal] = useState(0);
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null);
  const [broken, setBroken] = useState<Item[] | null>(null);
  const [brokenTotal, setBrokenTotal] = useState(0);
  const [refInput, setRefInput] = useState('');

  // What the answers are matched against, so a late answer to an old request is dropped
  const key = useRef({ query: '', category: 'all', family: 'all', group: 'all', mod: 'all' });
  key.current = { query, category, family, group, mod };
  const retry = useRef<number | undefined>(undefined);
  // A placement began from the editor: when its HUD goes away the editor comes back
  const awaiting = useRef<{ since: number; seen: boolean } | null>(null);
  const grace = useRef<number | undefined>(undefined);

  const request = useCallback((offset: number) => {
    setLoading(true);
    send({ action: 'search', ...key.current, offset, limit: PAGE });
  }, []);

  // Server answers and the editor's own comings and goings
  useEffect(() => {
    const onShow = () => {
      setVisible(true);
      send({ action: 'list' });
      send({ action: 'mapHideList' });
      send({ action: 'brokenList' });
    };
    const onHide = () => setVisible(false);
    const onResults = (e: Event) => {
      const d = (e as CustomEvent).detail as ResultsDetail;
      const k = key.current;
      if (d.query !== k.query || d.category !== k.category || d.family !== k.family || d.group !== k.group || d.mod !== k.mod) return;
      setReady(d.ready);
      setLoading(false);
      setTotal(d.total);
      setCounts(d.counts ?? {});
      setFamilies(d.families ?? []);
      setGroups(d.groups ?? []);
      setMods(d.mods ?? []);
      setItems((prev) => (d.offset === 0 ? d.results : d.offset === prev.length ? [...prev, ...d.results] : prev));
    };
    const onPlaced = (e: Event) => {
      const d = (e as CustomEvent).detail as { total: number; objects: PlacedEntry[] };
      setPlaced(d.objects ?? []);
      setPlacedTotal(d.total ?? 0);
      setConfirmRemove(null);
    };
    const onHidden = (e: Event) => {
      const d = (e as CustomEvent).detail as { total: number; objects: HiddenEntry[] };
      setHidden(d.objects ?? []);
      setHiddenTotal(d.total ?? 0);
      setConfirmRestore(null);
    };
    const onBroken = (e: Event) => {
      const d = (e as CustomEvent).detail as { total: number; objects: Item[] };
      setBroken(d.objects ?? []);
      setBrokenTotal(d.total ?? 0);
    };
    // The placement HUD shows while an object is being placed: its end brings the editor back
    const onHud = (e: Event) => {
      const a = awaiting.current;
      if (!a) return;
      const hud = (e as CustomEvent).detail as { visible?: boolean };
      if (hud.visible) {
        a.seen = true;
      } else if (a.seen) {
        awaiting.current = null;
        window.clearTimeout(grace.current);
        send({ action: 'reopen' });
      }
    };
    window.addEventListener('skymp5-client:showWorldEditor', onShow);
    window.addEventListener('skymp5-client:hideWorldEditor', onHide);
    window.addEventListener('skymp5-client:placementResults', onResults);
    window.addEventListener('skymp5-client:placementList', onPlaced);
    window.addEventListener('skymp5-client:mapHideList', onHidden);
    window.addEventListener('skymp5-client:placementBrokenList', onBroken);
    window.addEventListener('skymp5-client:placementHud', onHud);
    return () => {
      window.removeEventListener('skymp5-client:showWorldEditor', onShow);
      window.removeEventListener('skymp5-client:hideWorldEditor', onHide);
      window.removeEventListener('skymp5-client:placementResults', onResults);
      window.removeEventListener('skymp5-client:placementList', onPlaced);
      window.removeEventListener('skymp5-client:mapHideList', onHidden);
      window.removeEventListener('skymp5-client:placementBrokenList', onBroken);
      window.removeEventListener('skymp5-client:placementHud', onHud);
      window.clearTimeout(retry.current);
      window.clearTimeout(grace.current);
    };
  }, []);

  // Escape closes the editor (the browser has the focus while it is open)
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') send({ action: 'close' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  // A new query or filter starts the list again, after a short pause while typing
  useEffect(() => {
    if (!visible) return undefined;
    setItems([]);
    const t = window.setTimeout(() => request(0), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [visible, query, category, family, group, mod, request]);

  // The catalog is built in the background at server start: ask again until it is ready
  useEffect(() => {
    window.clearTimeout(retry.current);
    if (visible && !ready) retry.current = window.setTimeout(() => request(0), NOT_READY_RETRY_MS);
    return () => window.clearTimeout(retry.current);
  }, [visible, ready, request]);

  const updateStore = (next: Store) => {
    setStore(next);
    saveStore(next);
  };

  const isFav = (id: number) => store.fav.some((f) => f.id === id);
  const toggleFav = (item: Item) => {
    updateStore({ ...store, fav: isFav(item.id) ? store.fav.filter((f) => f.id !== item.id) : [slim(item), ...store.fav] });
  };

  const place = (item: Item) => {
    updateStore({ ...store, recent: [slim(item), ...store.recent.filter((r) => r.id !== item.id)].slice(0, MAX_RECENTS) });
    awaiting.current = { since: Date.now(), seen: false };
    window.clearTimeout(grace.current);
    grace.current = window.setTimeout(() => {
      const a = awaiting.current;
      if (a && !a.seen) {
        awaiting.current = null;
        send({ action: 'reopen' });
      }
    }, PLACEMENT_START_GRACE_MS);
    send({ action: 'start', id: item.id });
  };

  // The object does not place: put aside for everyone, out of the lists
  const putAside = (item: Item) => {
    send({ action: 'broken', id: item.id });
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    updateStore({ fav: store.fav.filter((f) => f.id !== item.id), recent: store.recent.filter((r) => r.id !== item.id) });
    setSelected(null);
    window.setTimeout(() => send({ action: 'brokenList' }), 400);
  };

  const pickCategory = (id: string) => {
    setCategory(id);
    setFamily('all');
    setGroup('all');
  };
  const pickFamily = (id: string) => {
    setFamily(id);
    setGroup('all');
  };

  // What the grid shows: the server's list, or the local shelf filtered by the query
  const shown: Item[] = useMemo(() => {
    if (shelf === 'all') return items;
    const source = shelf === 'fav' ? store.fav : store.recent;
    const q = query.trim().toLowerCase();
    return q ? source.filter((i) => (i.n + ' ' + i.edid).toLowerCase().includes(q)) : source;
  }, [shelf, items, store, query]);

  const allCount = Object.keys(counts).reduce((n, k) => n + (counts[k] || 0), 0);

  if (!visible) return null;

  const familyLabel = families.find((f) => f.id === family)?.label ?? family;
  const groupLabel = groups.find((g) => g.id === group)?.label ?? group;
  const trail = [
    category === 'all' ? 'Tout le catalogue' : categoryLabel(category),
    ...(family !== 'all' ? [familyLabel] : []),
    ...(group !== 'all' ? [groupLabel] : []),
  ];

  const treeButton = (label: string, count: number | null, active: boolean, depth: 0 | 1 | 2, onClick: () => void) => (
    <button
      key={label + depth}
      type="button"
      className={`world-editor__node world-editor__node--d${depth}${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      <span className="world-editor__node-label">{label}</span>
      {count !== null ? <span className="world-editor__count">{count}</span> : null}
    </button>
  );

  const catalogView = (
    <div className="world-editor__split">
      <nav className="world-editor__tree" aria-label="Organisation du catalogue">
        <div className="world-editor__shelves" role="tablist" aria-label="Où chercher">
          {([['all', 'Catalogue'], ['fav', `Favoris ${store.fav.length}`], ['recent', 'Récents']] as Array<[Shelf, string]>).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={shelf === id}
              className={'world-editor__shelf' + (shelf === id ? ' is-active' : '')}
              onClick={() => setShelf(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {shelf === 'all' ? (
          <>
            <div className="world-editor__nodes">
              {treeButton('Tout le catalogue', allCount, category === 'all', 0, () => pickCategory('all'))}
              {CATEGORIES.map((c) => (
                <React.Fragment key={c.id}>
                  {treeButton(c.label, counts[c.id] || 0, category === c.id && family === 'all', 0, () => pickCategory(c.id))}
                  {category === c.id
                    ? families.map((f) => (
                        <React.Fragment key={f.id}>
                          {treeButton(f.label, f.count, family === f.id && group === 'all', 1, () => pickFamily(f.id))}
                          {family === f.id
                            ? groups.map((g) => treeButton(g.label, g.count, group === g.id, 2, () => setGroup(g.id)))
                            : null}
                        </React.Fragment>
                      ))
                    : null}
                </React.Fragment>
              ))}
            </div>

            <label className="world-editor__source">
              <span>Source</span>
              <select value={mod} onChange={(e) => setMod(e.target.value)}>
                <option value="all">Tous les plugins</option>
                {mod !== 'all' && !mods.some((m) => m.id === mod) ? <option value={mod}>{mod}</option> : null}
                {mods.map((m) => (
                  <option key={m.id} value={m.id}>{m.label} ({m.count})</option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <p className="world-editor__hint">
            {shelf === 'fav' ? 'Tes objets favoris, gardés d\'une session à l\'autre.' : 'Les derniers objets que tu as posés.'}
          </p>
        )}
      </nav>

      <div className="world-editor__content">
        <div className="world-editor__search">
          <input
            autoFocus
            placeholder="Chercher : forge, chaise, bougie, tonneau, mur..."
            value={query}
            maxLength={60}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && selected) place(selected);
            }}
          />
          {query ? (
            <button type="button" className="world-editor__clear" onClick={() => setQuery('')} aria-label="Effacer la recherche">
              Effacer
            </button>
          ) : null}
        </div>

        <div className="world-editor__trail">
          <span className="world-editor__trail-path">{shelf === 'all' ? trail.join('  /  ') : shelf === 'fav' ? 'Favoris' : 'Récents'}</span>
          <span className="world-editor__trail-count">{shelf === 'all' ? `${total} objet${total > 1 ? 's' : ''}` : `${shown.length}`}</span>
        </div>

        <div className="world-editor__grid-wrap">
          {shelf === 'all' && !ready ? (
            <div className="world-editor__empty">Le catalogue se construit au démarrage du serveur, quelques secondes...</div>
          ) : shown.length === 0 ? (
            <div className="world-editor__empty">
              {shelf === 'fav'
                ? 'Aucun favori. Clique l\'étoile d\'un objet pour le retrouver ici.'
                : shelf === 'recent'
                  ? 'Rien posé récemment. Les objets que tu poses apparaissent ici.'
                  : loading
                    ? 'Chargement...'
                    : 'Aucun objet ne correspond. Essaie un autre mot (les noms du jeu sont en anglais) ou élargis le filtre.'}
            </div>
          ) : (
            <>
              <ul className="world-editor__grid">
                {shown.map((o) => {
                  const name = prettify(o);
                  return (
                    <li key={o.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={'world-editor__card' + (selected?.id === o.id ? ' is-selected' : '')}
                        onClick={() => setSelected(o)}
                        onDoubleClick={() => place(o)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') place(o);
                          else if (e.key === ' ') {
                            e.preventDefault();
                            setSelected(o);
                          }
                        }}
                      >
                        <span className="world-editor__tile" aria-hidden="true">{monogram(name)}</span>
                        <span className="world-editor__card-body">
                          <span className="world-editor__card-name">{name}</span>
                          <span className="world-editor__card-meta">{o.mod}</span>
                        </span>
                        <button
                          type="button"
                          className={'world-editor__star' + (isFav(o.id) ? ' is-on' : '')}
                          aria-label={isFav(o.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                          aria-pressed={isFav(o.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFav(o);
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <polygon points="12,2.5 14.9,8.9 21.8,9.6 16.6,14.2 18.1,21 12,17.5 5.9,21 7.4,14.2 2.2,9.6 9.1,8.9" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {shelf === 'all' && total > items.length ? (
                <button type="button" className="world-editor__more" disabled={loading} onClick={() => request(items.length)}>
                  {loading ? 'Chargement...' : `Afficher la suite (${items.length} sur ${total})`}
                </button>
              ) : null}
            </>
          )}
        </div>

        <div className="world-editor__dock">
          {selected ? (
            <div className="world-editor__picked">
              <span className="world-editor__picked-name">{prettify(selected)}</span>
              <span className="world-editor__picked-meta">
                {categoryLabel(selected.c)} - {selected.edid !== selected.n ? `${selected.edid} - ` : ''}{selected.mod}
              </span>
            </div>
          ) : (
            <div className="world-editor__picked world-editor__picked--none">
              Un clic choisit un objet, un double-clic le pose.
            </div>
          )}
          {selected ? (
            <button
              type="button"
              className="world-editor__btn world-editor__btn--quiet"
              title="L'aperçu ne s'affiche pas ou l'objet est invisible : il est écarté du catalogue pour tous"
              onClick={() => putAside(selected)}
            >
              Ne se pose pas
            </button>
          ) : null}
          <button type="button" className="world-editor__go" disabled={!selected} onClick={() => selected && place(selected)}>
            Poser
          </button>
        </div>
        <p className="world-editor__sizehint">
          Pendant la pose : Pavé 9 agrandit, Pavé 7 réduit, Pavé 1 rend la taille normale. À la souris : Ctrl + molette sur le repère jaune.
        </p>
      </div>
    </div>
  );

  const q = placedFilter.trim().toLowerCase();
  const placedShown = (placed ?? []).filter((o) => !q || o.n.toLowerCase().includes(q) || o.by.toLowerCase().includes(q));

  const placedView = (
    <>
      <div className="world-editor__search">
        <input placeholder="Filtrer les objets posés (nom ou auteur)" value={placedFilter} maxLength={40} onChange={(e) => setPlacedFilter(e.target.value)} />
      </div>
      <div className="world-editor__list">
        {placed === null ? (
          <div className="world-editor__empty">Chargement...</div>
        ) : placedShown.length === 0 ? (
          <div className="world-editor__empty">{placed.length === 0 ? 'Aucun objet posé avec cet éditeur.' : 'Aucun objet ne correspond.'}</div>
        ) : (
          placedShown.map((o) => (
            <div key={o.r} className="world-editor__row">
              <div className="world-editor__row-body">
                <span className="world-editor__row-name">{o.n}{o.sc ? ` (taille x${o.sc})` : ''}</span>
                <span className="world-editor__row-meta">
                  {categoryLabel(o.c)} - {o.by}{o.at ? ` - ${formatDate(o.at)}` : ''}
                </span>
              </div>
              <button type="button" className="world-editor__btn" onClick={() => send({ action: 'goto', refrId: o.r })}>Aller</button>
              <button
                type="button"
                className="world-editor__btn world-editor__btn--accent"
                title="Affiche l'objet pour le déplacer, le tourner ou changer sa taille"
                onClick={() => send({ action: 'move', refrId: o.r })}
              >
                Modifier
              </button>
              {confirmRemove === o.r ? (
                <>
                  <button
                    type="button"
                    className="world-editor__btn world-editor__btn--danger"
                    onClick={() => { send({ action: 'remove', refrId: o.r }); setConfirmRemove(null); }}
                  >
                    Confirmer
                  </button>
                  <button type="button" className="world-editor__btn world-editor__btn--quiet" onClick={() => setConfirmRemove(null)}>Annuler</button>
                </>
              ) : (
                <button type="button" className="world-editor__btn world-editor__btn--danger" onClick={() => setConfirmRemove(o.r)}>Supprimer</button>
              )}
            </div>
          ))
        )}
        {placed && placedTotal > placed.length ? (
          <div className="world-editor__empty">Les {placed.length} plus récents sur {placedTotal}.</div>
        ) : null}
      </div>
    </>
  );

  const mapView = (
    <>
      <div className="world-editor__tools">
        <button type="button" className="world-editor__tool" onClick={() => send({ action: 'mapHideStart' })}>
          <span className="world-editor__tool-title">Masquer un objet du décor</span>
          <span className="world-editor__tool-text">Ouvre la console (~), clique l'objet, referme la console. Une fenêtre confirme.</span>
        </button>
        <button type="button" className="world-editor__tool" onClick={() => send({ action: 'mapCopyStart' })}>
          <span className="world-editor__tool-title">Retrouver un objet du monde</span>
          <span className="world-editor__tool-text">Clique un objet en console : un exemplaire neuf du même objet passe en aperçu.</span>
        </button>
      </div>
      <div className="world-editor__search world-editor__search--inline">
        <input
          placeholder="Ou le RefID de la console (ex. 32023821)"
          value={refInput}
          maxLength={10}
          onChange={(e) => setRefInput(e.target.value.replace(/[^0-9a-fA-Fx]/g, ''))}
        />
        <button
          type="button"
          className="world-editor__btn world-editor__btn--accent"
          disabled={refInput.replace(/^0x/i, '').length === 0}
          onClick={() => { send({ action: 'mapHideById', refId: refInput }); setRefInput(''); }}
        >
          Masquer
        </button>
      </div>
      <div className="world-editor__list">
        {hidden === null || hidden.length === 0 ? (
          <div className="world-editor__empty">Aucun objet masqué.</div>
        ) : (
          hidden.map((o) => (
            <div key={o.r} className="world-editor__row">
              <div className="world-editor__row-body">
                <span className="world-editor__row-name">{o.n}</span>
                <span className="world-editor__row-meta">
                  {o.mod ? `${o.mod} - ` : ''}{o.r.toString(16)} - {o.by}{o.at ? ` - ${formatDate(o.at)}` : ''}
                </span>
              </div>
              <button type="button" className="world-editor__btn" onClick={() => send({ action: 'mapHideGoto', refId: o.r })}>Aller</button>
              {confirmRestore === o.r ? (
                <>
                  <button
                    type="button"
                    className="world-editor__btn world-editor__btn--danger"
                    onClick={() => { send({ action: 'mapHideRestore', refId: o.r }); setConfirmRestore(null); }}
                  >
                    Confirmer
                  </button>
                  <button type="button" className="world-editor__btn world-editor__btn--quiet" onClick={() => setConfirmRestore(null)}>Annuler</button>
                </>
              ) : (
                <button type="button" className="world-editor__btn" onClick={() => setConfirmRestore(o.r)}>Remettre</button>
              )}
            </div>
          ))
        )}
        {hidden && hiddenTotal > hidden.length ? (
          <div className="world-editor__empty">Les {hidden.length} plus récents sur {hiddenTotal}.</div>
        ) : null}
      </div>
    </>
  );

  const brokenView = (
    <>
      <p className="world-editor__hint">
        Objets écartés du catalogue parce qu'ils ne se posent pas (aperçu impossible ou objet invisible). Remets-en un pour le
        proposer de nouveau.
      </p>
      <div className="world-editor__list">
        {broken === null || broken.length === 0 ? (
          <div className="world-editor__empty">Aucun objet écarté.</div>
        ) : (
          broken.map((o) => (
            <div key={o.id} className="world-editor__row">
              <div className="world-editor__row-body">
                <span className="world-editor__row-name">{prettify(o)}</span>
                <span className="world-editor__row-meta">{categoryLabel(o.c)} - {o.edid} - {o.mod}</span>
              </div>
              <button
                type="button"
                className="world-editor__btn"
                onClick={() => {
                  send({ action: 'restoreBroken', id: o.id });
                  setBroken((prev) => (prev ? prev.filter((b) => b.id !== o.id) : prev));
                }}
              >
                Remettre
              </button>
            </div>
          ))
        )}
        {broken && brokenTotal > broken.length ? (
          <div className="world-editor__empty">Les {broken.length} premiers sur {brokenTotal}.</div>
        ) : null}
      </div>
    </>
  );

  const modes: Array<[Mode, string]> = [
    ['catalog', 'Catalogue'],
    ['placed', `Posés${placed ? ` (${placedTotal})` : ''}`],
    ['map', `Carte${hidden ? ` (${hiddenTotal})` : ''}`],
    ['broken', `Écartés${broken ? ` (${brokenTotal})` : ''}`],
  ];

  return (
    <aside className="world-editor" aria-label="Éditeur du monde">
      <header className="world-editor__head">
        <div>
          <h2 className="world-editor__title">Éditeur du monde</h2>
          <p className="world-editor__sub-title">Réservé au staff</p>
        </div>
        <button type="button" className="world-editor__close" onClick={() => send({ action: 'close' })}>
          Fermer
        </button>
      </header>

      <nav className="world-editor__modes" role="tablist" aria-label="Sections">
        {modes.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mode === id}
            className={'world-editor__mode' + (mode === id ? ' is-active' : '')}
            onClick={() => {
              setMode(id);
              if (id === 'placed') send({ action: 'list' });
              if (id === 'map') send({ action: 'mapHideList' });
              if (id === 'broken') send({ action: 'brokenList' });
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="world-editor__body">
        {mode === 'catalog' ? catalogView : mode === 'placed' ? placedView : mode === 'map' ? mapView : brokenView}
      </div>

      <footer className="world-editor__foot">Échap ou la touche d'ouverture : fermer.</footer>
    </aside>
  );
};

export default WorldEditor;
